import React, { useMemo } from "react";
import { siteImage } from "../service/pspricesDeals.service";
import { useFavorites } from "./FavoritesContext";
import HeartButton from "./HeartButton";
import { favoriteFromDeal, keyOf } from "./favoriteKey";
import { useFavoritePrices } from "./useFavoritePrices";

function rub(value) {
  return `${Math.round(value).toLocaleString("ru-RU")} ₽`;
}

function money(value, currency) {
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency,
      currencyDisplay: currency === "KZT" ? "narrowSymbol" : "symbol",
    }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

/**
 * «Ваше избранное» над сеткой витрины. Скидка — по цене региона витрины: из самой
 * витрины, а для Nintendo и Steam ещё и из каталога цен. PlayStation — только с витрины.
 * onOpen(item, deal, price) открывает покупку; deal — карточка витрины, если игра на ней есть,
 * price — цена из каталога в регионе витрины (или null).
 */
function FavoritesStrip({ platform, region, deals, onOpen }) {
  const { items } = useFavorites();
  const mine = useMemo(
    () => items.filter((item) => item.platform === platform),
    [items, platform],
  );
  const dealByKey = useMemo(() => {
    const map = new Map();
    deals.forEach((deal) => {
      const item = favoriteFromDeal(platform, deal, region);
      if (item) map.set(keyOf(item), deal);
    });
    return map;
  }, [deals, platform, region]);
  const priceItems = useMemo(
    () =>
      platform === "playstation"
        ? []
        : mine
            .filter((item) => !dealByKey.has(keyOf(item)))
            .map((item) => ({ ...item, region })),
    [mine, dealByKey, platform, region],
  );
  const { prices } = useFavoritePrices(priceItems);

  if (!mine.length) return null;

  const rows = mine
    .map((item, index) => {
      const deal = dealByKey.get(keyOf(item));
      const price = deal
        ? {
            percent: deal.discountPercent,
            amount: deal.amount,
            currency: deal.currency,
          }
        : prices[keyOf(item)]
          ? {
              percent: prices[keyOf(item)].percent,
              amount: prices[keyOf(item)].price,
              currency: prices[keyOf(item)].currency,
              rub: prices[keyOf(item)].rub,
            }
          : null;
      return { item, deal, price, current: prices[keyOf(item)], index };
    })
    .sort((a, b) => (b.price?.percent ? 1 : 0) - (a.price?.percent ? 1 : 0) || a.index - b.index);

  return (
    <section className="fav-strip" aria-label="Ваше избранное">
      <h2 className="fav-strip-title">Ваше избранное</h2>
      <div className="fav-strip-row">
        {rows.map(({ item, deal, price, current }) => (
          <div key={keyOf(item)} className="fav-strip-card">
            <button
              type="button"
              onClick={() => onOpen(item, deal, current)}
              className="block w-full text-left"
            >
              {item.image ? (
                <img
                  src={siteImage(item.image)}
                  alt=""
                  loading="lazy"
                  className={`w-full bg-gray-100 object-cover dark:bg-gray-700 ${
                    platform === "steam" ? "aspect-[460/215]" : "aspect-square"
                  }`}
                />
              ) : (
                <div className="aspect-square w-full bg-gray-200 dark:bg-gray-700" />
              )}
              <span className="block h-[2.6em] overflow-hidden px-2 pt-1.5 text-xs leading-tight text-gray-900 dark:text-white">
                {item.title}
              </span>
              <span className="block px-2 pb-2 pt-0.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                {price?.percent ? (
                  <span className="fav-strip-sale">сейчас со скидкой −{price.percent}%</span>
                ) : null}
                {price?.amount != null
                  ? price.rub && price.currency !== "RUB"
                    ? `${money(price.amount, price.currency)} · ≈ ${rub(price.rub)}`
                    : money(price.amount, price.currency)
                  : "\u00a0"}
              </span>
            </button>
            <HeartButton item={item} className="absolute right-1 top-1 !h-7 !w-7" />
          </div>
        ))}
      </div>
    </section>
  );
}

export default FavoritesStrip;
